import { Vector3, Vector4 } from "@babylonjs/core/Maths/math.vector.js";
import { DcRenderingGroup } from "./DcRenderingGroup";
import { Env } from "../settings/Env";
import { CustomMaterial } from "@babylonjs/materials/custom/customMaterial.js";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color.js";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial.js";
import { SceneLoader } from "@babylonjs/core/Loading/sceneLoader.js";
import { Texture } from "@babylonjs/core/Materials/Textures/texture.js";
import { FresnelParameters } from "@babylonjs/core/Materials/fresnelParameters.js";
export class Logo {
    constructor(scene, guiRootNode) {
        this._isInitialized = false;
        this._isVisible = false;
        this._time = 0.0;
        this._showingTime = undefined;
        this._hidingTime = undefined;
        this._bindMaterialHandler = () => {
            const phase = (this._time / Logo._periodMillis + 1) % 3;
            this._meshes.headset?.material?.getEffect()?.setFloat("currentTime", phase);
        };
        this._rotateAnimation = () => {
            if (!this._isVisible)
                return;
            this._time += this._scene.deltaTime ?? 0;
            // let alpha = -2.0 * Math.PI * this._time / Logo._periodMillis;
            const alpha = Math.PI / 4;
            const x = Logo._radius * Math.cos(alpha);
            const y = Logo._radius * Math.sin(alpha);
            const direction = new Vector3(x, y, -Logo._distance);
            this._meshes.root?.lookAt(this._meshes.root.position.add(direction));
        };
        this._fadingAnimation = () => {
            if (this._showingTime === undefined && this._hidingTime === undefined)
                return;
            const currentTime = this._time;
            let alpha = undefined;
            if (this._showingTime) {
                if (currentTime >= this._showingTime.finish) {
                    this._scene.unregisterBeforeRender(this._fadingAnimation);
                    this._showingTime = undefined;
                }
                alpha = this._showingTime ? (currentTime - this._showingTime.start) / this._showingTime.duration : 1.0;
            }
            else if (this._hidingTime) {
                if (currentTime >= this._hidingTime.finish) {
                    this._scene.unregisterBeforeRender(this._fadingAnimation);
                    this._scene.unregisterBeforeRender(this._rotateAnimation);
                    this._meshes.headset?.material?.onBindObservable.removeCallback(this._bindMaterialHandler);
                    this._hidingTime = undefined;
                    this._isVisible = false;
                    this._meshes.all.map((m) => m.isVisible = false);
                }
                alpha = this._hidingTime ? (this._hidingTime.finish - currentTime) / this._hidingTime.duration : 1.0;
            }
            if (alpha !== undefined) {
                this._meshes.all.map(m => {
                    if (m.material)
                        m.material.alpha = alpha;
                });
            }
        };
        this._scene = scene;
        this._guiRootNode = guiRootNode;
        this._meshes = { all: [] };
    }
    async initAsync() {
        if (this._isInitialized)
            return;
        const loadedLogoModel = await SceneLoader.ImportMeshAsync(null, Env.logo.assetsUrl, Env.logo.fileName, this._scene);
        if (loadedLogoModel.meshes.length === 0)
            return;
        this._meshes.all = [];
        loadedLogoModel.meshes.map(m => {
            m.isPickable = false;
            this._meshes.all.push(m);
            m.renderingGroupId = DcRenderingGroup.logo;
            m.isVisible = false;
        });
        const rootMesh = loadedLogoModel.meshes[0];
        rootMesh.name = "logo";
        rootMesh.scaling = new Vector3(-1, 1, 1);
        rootMesh.position = new Vector3(0, 0, 0.7);
        rootMesh.parent = this._guiRootNode;
        this._meshes.root = rootMesh;
        const headsetMesh = loadedLogoModel.meshes.find(x => x.name === "Headset");
        if (headsetMesh) {
            const animatedMaterial = this._createAnimatedMaterial();
            animatedMaterial.disableLighting = true;
            headsetMesh.material = animatedMaterial;
            this._meshes.headset = headsetMesh;
        }
        const whiteMaterial = new StandardMaterial("whiteMaterial", this._scene);
        whiteMaterial.emissiveColor = Color3.White();
        this._meshes
            .all
            .filter(mat => mat.name !== rootMesh.name && headsetMesh && mat.name !== headsetMesh.name)
            .map(w => w.material = whiteMaterial);
        this._isVisible = false;
        this._isInitialized = true;
    }
    show(duration) {
        if (this._isVisible)
            return;
        this._time = 0.0;
        this._scene.registerBeforeRender(this._rotateAnimation);
        this._meshes.headset?.material?.onBindObservable.add(this._bindMaterialHandler);
        this._isVisible = true;
        this._meshes.all.map((m) => {
            if (m.material)
                m.material.alpha = duration === undefined ? 1 : 0;
            m.isVisible = true;
        });
        if (duration !== undefined) {
            this._showingTime = {
                duration: duration,
                start: this._time,
                finish: this._time + duration,
            };
            this._scene.registerBeforeRender(this._fadingAnimation);
        }
    }
    hide(duration) {
        if (!this._isVisible)
            return;
        this._hidingTime = {
            duration: duration,
            start: this._time,
            finish: this._time + duration,
        };
        this._scene.registerBeforeRender(this._fadingAnimation);
    }
    _createAnimatedMaterial() {
        const result = new CustomMaterial("HeadsetMaterial", this._scene);
        result.diffuseTexture = new Texture(Env.logo.texture, this._scene);
        result.emissiveColor = Color3.White();
        result.emissiveFresnelParameters = new FresnelParameters({
            leftColor: Color3.Black(),
            rightColor: Color3.White()
        });
        result.Fragment_Custom_Alpha(`
            int idx = 0;
            float curTimestamp = 0.0;
            float nextTimestamp = 0.0;
            vec4 curLeftColor;
            vec4 nextLeftColor;
            vec4 curRightColor;
            vec4 nextRightColor;
            if(currentTime >= timestamps0 && currentTime < timestamps1)
            {
                curTimestamp = timestamps0;
                nextTimestamp = timestamps1;
                curLeftColor = colorsLeft0;
                nextLeftColor = colorsLeft1;
                curRightColor = colorsRight0;
                nextRightColor = colorsRight1;
            }
            else if(currentTime >= timestamps1 && currentTime < timestamps2)
            {
                curTimestamp = timestamps1;
                nextTimestamp = timestamps2;
                curLeftColor = colorsLeft1;
                nextLeftColor = colorsLeft2;
                curRightColor = colorsRight1;
                nextRightColor = colorsRight2;
            }
            else if(currentTime >= timestamps2 && currentTime < timestamps3)
            {
                curTimestamp = timestamps2;
                nextTimestamp = timestamps3;
                curLeftColor = colorsLeft2;
                nextLeftColor = colorsLeft3;
                curRightColor = colorsRight2;
                nextRightColor = colorsRight3;
            }
            else if(currentTime >= timestamps3 && currentTime < timestamps4)
            {
                curTimestamp = timestamps3;
                nextTimestamp = timestamps4;
                curLeftColor = colorsLeft3;
                nextLeftColor = colorsLeft4;
                curRightColor = colorsRight3;
                nextRightColor = colorsRight4;
            }
            float timePercentage = (currentTime - curTimestamp)/(nextTimestamp - curTimestamp);
            vec4 leftColor = mix(curLeftColor, nextLeftColor, timePercentage);
            vec4 rightColor = mix(curRightColor, nextRightColor, timePercentage);
            baseColor = mix(leftColor, rightColor, vDiffuseUV.x);
        `);
        const pink = Color4.FromHexString("#D62198");
        const blue = Color4.FromHexString("#2787FA");
        const pinkVector = new Vector4(pink.r, pink.g, pink.b, pink.a);
        const blueVector = new Vector4(blue.r, blue.g, blue.b, blue.a);
        result.AddUniform("colorsLeft0", "vec4", pinkVector);
        result.AddUniform("colorsLeft1", "vec4", blueVector);
        result.AddUniform("colorsLeft2", "vec4", blueVector);
        result.AddUniform("colorsLeft3", "vec4", pinkVector);
        result.AddUniform("colorsLeft4", "vec4", pinkVector);
        result.AddUniform("colorsRight0", "vec4", pinkVector);
        result.AddUniform("colorsRight1", "vec4", pinkVector);
        result.AddUniform("colorsRight2", "vec4", blueVector);
        result.AddUniform("colorsRight3", "vec4", blueVector);
        result.AddUniform("colorsRight4", "vec4", pinkVector);
        result.AddUniform("timestamps0", "float", 0);
        result.AddUniform("timestamps1", "float", 0.75);
        result.AddUniform("timestamps2", "float", 1.5);
        result.AddUniform("timestamps3", "float", 2.25);
        result.AddUniform("timestamps4", "float", 3);
        result.AddUniform("currentTime", "float", 0);
        return result;
    }
}
Logo._radius = 2;
Logo._distance = 10;
Logo._periodMillis = 2000;
